import React, { useMemo, useCallback } from "react";
import { usePromise } from "react-use";
import withStyles from "@material-ui/core/styles/withStyles";
import { withRouter } from "react-router-dom";
import * as yup from "yup";
import { withAuth, requiredString, PropertyForm, NavItem } from "../../common";

const styles = theme => ({
  paper: {
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      minHeight: "calc(100vh - 90px)"
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "calc(100vh - 90px)"
    },
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(2)
    }
  },
  toolbar: {
    ...theme.mixins.toolbar,
    [theme.breakpoints.up("md")]: {
      minHeight: "90px !important"
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: "90px !important"
    }
  }
});

const form = {
  title: "Password Reset",
  section: { marginTop: 1 },
  field: { padding: 1 },
  hideCancel: true,
  hideReset: true,
  hideSave: true,
  defaultValues: {
    password: null,
    confirm: null,
    sent: false
  },
  validationSchema: yup.object().shape({
    password: requiredString,
    confirm: requiredString.oneOf(
      [yup.ref("password"), null],
      "Passwords must match"
    )
  }),
  fieldInfo: {
    status: {
      editor: "status"
    },
    info: {
      editor: "content",
      content: "Please enter your new password."
    },
    password: {
      label: "Password",
      autoComplete: "new-password",
      type: "password",
      required: true
    },
    confirm: {
      label: "Confirm Password",
      autoComplete: "new-password",
      type: "password",
      required: true
    },
    submit: {
      editor: "submit",
      label: "Change Password",
      color: "primary",
      variant: "contained"
    },
    sentInfo: {
      editor: "content"
    },
    getStarted: {
      editor: "content",
      content: () => <NavItem url="/login">Get Started</NavItem>
    }
  },
  sections: [
    {
      layout: [["status"]]
    },
    {
      hidden: vals => vals.sent,
      layout: [["info"], ["password"], ["confirm"], ["submit"]]
    },
    {
      hidden: vals => !vals.sent,
      layout: [["sentInfo"], ["getStarted"]]
    }
  ]
};

const ChangePassword = ({
  classes,
  changePassword,
  location: { search },
  ...rest
}) => {
  const mounted = usePromise();
  const sig = useMemo(() => new URLSearchParams(search).get("signature"), [
    search
  ]);

  const onSubmit = useCallback(
    function onSubmit(vals, form) {
      mounted(changePassword(vals.password, sig))
        .then(result => {
          if (result.success) {
            form.setFieldValue("sentInfo", "Your password has been changed.");
            form.setFieldValue("sent", true);
            form.setStatus(undefined);
            form.setErrors({});
          } else {
            if (typeof result.message === "string")
              form.setStatus(result.message);
            else {
              const errs = { ...result.message };
              if (result.message.new_password) {
                const passErrs = [];
                result.message.new_password.forEach(err => {
                  Object.keys(err).forEach(errKey => {
                    passErrs.push(`${errKey}: ${err[errKey]}`);
                  });
                });
                errs.password = passErrs.join(", ");
              }
              form.setErrors(errs);
            }
          }
          form.setSubmitting(false);
        })
        .catch(err => {
          form.setFieldValue(
            "sentInfo",
            "An error has occurred.  Please try later."
          );
          form.setFieldValue("sent", true);
          form.setSubmitting(false);
        });
    },
    [changePassword, sig, mounted]
  );

  return (
    <div className={classes.paper}>
      <div className={classes.toolbar} />

      <PropertyForm {...form} onSubmit={onSubmit} />
    </div>
  );
};

export default withRouter(withStyles(styles)(withAuth(ChangePassword)));
